import { BrowserRouter as Router, Routes, Route, useLocation } from "react-router-dom";
import { useEffect, lazy, Suspense } from "react";
import Layout from "./components/common/Layout";
import HomepageScreen from "./pages/homeIndex";
import SignupOfferPopup from "./components/common/SignupOfferPopup";
import AdminRoute from "./components/AdminRoute";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// Lazy-load non-landing routes to reduce the initial JS bundle (faster home-page load).
const AboutPage = lazy(() => import("./pages/aboutIndex"));
const FrostPineChaletPage = lazy(() => import("./pages/propertyIndex"));
const ContactPage = lazy(() => import("./pages/Contact"));
const BookingPage = lazy(() => import("./pages/BookingPage"));
const SearchPage = lazy(() => import("./pages/SearchPage"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsConditions = lazy(() => import("./pages/TermsConditions"));
const FAQPage = lazy(() => import("./pages/FAQPage"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const GalleryPage = lazy(() => import("./pages/GalleryPage"));
const NewryMainePage = lazy(() => import("./pages/NewryMainePage"));
const BethelMainePage = lazy(() => import("./pages/BethelMainePage"));
const NotFound = lazy(() => import("./pages/NotFound"));

// Admin (CMS) screens — rendered without the public site Layout.
const AdminLogin = lazy(() => import("./pages/admin/AdminLogin"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminBlogForm = lazy(() => import("./pages/admin/AdminBlogForm"));

// ScrollToTop component
const ScrollToTop = () => {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return null;
};

const AppContent = () => {
  const location = useLocation();
  const isAdmin = location.pathname.startsWith("/admin");

  // Admin area: no public header/footer/popup.
  if (isAdmin) {
    return (
      <Suspense fallback={null}>
        <Routes>
          <Route path="/admin" element={<AdminLogin />} />
          <Route
            path="/admin/home"
            element={<AdminRoute><AdminDashboard /></AdminRoute>}
          />
          <Route
            path="/admin/add"
            element={<AdminRoute><AdminBlogForm /></AdminRoute>}
          />
          <Route
            path="/admin/edit/:slug"
            element={<AdminRoute><AdminBlogForm /></AdminRoute>}
          />
        </Routes>
      </Suspense>
    );
  }

  // Public site.
  return (
    <>
      <SignupOfferPopup />
      <Layout>
        <Suspense fallback={null}>
          <Routes>
            <Route path="/" element={<HomepageScreen />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/property/:slug" element={<FrostPineChaletPage />} />
            <Route path="/booking" element={<BookingPage />} />
            <Route path="/search" element={<SearchPage />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsConditions />} />
            <Route path="/faqs" element={<FAQPage />} />
            <Route path="/frequently-asked-questions" element={<FAQPage />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/gallery" element={<GalleryPage />} />
            <Route path="/rentals/newry-maine" element={<NewryMainePage />} />
            <Route path="/rentals/bethel-maine" element={<BethelMainePage />} />
            <Route path="/404" element={<NotFound />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </Layout>
    </>
  );
};

function App() {
  return (
    <Router>
      <ScrollToTop />
      <AppContent />
      <ToastContainer position="top-right" autoClose={3000} newestOnTop />
    </Router>
  );
}

export default App;
